/**
 * PasswordStrengthBar Component
 *
 * KC-style 4-segment visual indicator of password strength.
 * Scores password on length, case, digits, and special characters.
 *
 * @module pages/SettingsPage/components/PasswordStrengthBar
 */

import { type ReactElement, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './PasswordStrengthBar.module.css';

interface PasswordStrengthBarProps {
  password: string;
}

const MIN_LENGTH = 8;
const STRONG_LENGTH = 12;
const SPECIAL_CHARACTERS = '!@#$%^&*()_+-=[]{}|;:,.<>?';
const SEGMENT_COUNT = 4;

const HAS_LOWERCASE_RE = /[a-z]/;
const HAS_UPPERCASE_RE = /[A-Z]/;
const HAS_DIGIT_RE = /\d/;

const STRENGTH_LABELS = ['weak', 'fair', 'good', 'strong'] as const;

const STRENGTH_LEVELS = [
  { maxScore: 1, labelKey: STRENGTH_LABELS[0], activeSegments: 1 },
  { maxScore: 2, labelKey: STRENGTH_LABELS[1], activeSegments: 2 },
  { maxScore: 3, labelKey: STRENGTH_LABELS[2], activeSegments: 3 },
  { maxScore: 5, labelKey: STRENGTH_LABELS[3], activeSegments: 4 },
] as const;

function calculatePasswordScore(password: string): number {
  let score = 0;

  if (password.length >= MIN_LENGTH) score += 1;
  if (password.length >= STRONG_LENGTH) score += 1;
  if (HAS_LOWERCASE_RE.test(password) && HAS_UPPERCASE_RE.test(password))
    score += 1;
  if (HAS_DIGIT_RE.test(password)) score += 1;

  const hasSpecial = [...password].some((character) =>
    SPECIAL_CHARACTERS.includes(character)
  );
  if (hasSpecial) score += 1;

  return score;
}

function getStrengthLevel(
  score: number
): (typeof STRENGTH_LEVELS)[number] | null {
  if (score === 0) return null;

  const matched = STRENGTH_LEVELS.find((level) => score <= level.maxScore);
  return matched ?? STRENGTH_LEVELS[STRENGTH_LEVELS.length - 1] ?? null;
}

export const PasswordStrengthBar = memo(function PasswordStrengthBar({
  password,
}: PasswordStrengthBarProps): ReactElement | null {
  const { t } = useTranslation();

  const score = useMemo(() => calculatePasswordScore(password), [password]);
  const strengthLevel = useMemo(() => getStrengthLevel(score), [score]);

  if (!strengthLevel) {
    return null;
  }

  const { activeSegments, labelKey } = strengthLevel;
  const strengthValue = String(activeSegments);

  return (
    <div className={styles.container}>
      <div className={styles.segments}>
        {Array.from({ length: SEGMENT_COUNT }, (_, index) => (
          <div
            key={index}
            className={styles.segment}
            data-active={index < activeSegments ? 'true' : 'false'}
            data-strength={index < activeSegments ? strengthValue : undefined}
          />
        ))}
      </div>
      <span className={styles.label} data-strength={strengthValue}>
        {t(`settings.passwordStrength.${labelKey}`, {
          count: password.length,
        })}
      </span>
    </div>
  );
});
